import { signInStudent, signUpStudent, signInStaff, signUpStaff, getUserProfile, sendPasswordResetForStudent, sendPasswordResetForStaff } from "../services/auth.js";

function showToast(message, type = "info") {
  if (typeof window.showToast === "function") {
    window.showToast(message, type);
  } else {
    const toast = document.createElement("div");
    toast.style.cssText = `
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      background: ${type === "success" ? "#22c55e" : type === "error" ? "#ef4444" : "#0B1B5E"};
      color: #fff;
      padding: 16px 24px;
      border-radius: 12px;
      font-weight: 600;
      font-size: 0.9rem;
      z-index: 10001;
      box-shadow: 0 8px 24px rgba(0,0,0,0.2);
      animation: slideUp 0.3s ease;
      max-width: 90%;
      text-align: center;
    `;
    toast.textContent = message;
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 3000);
  }
}

/**
 * Componente: Landing Page (landing.js)
 * Controla os efeitos dinâmicos, lazy loading de imagens, animações ao scroll
 * e atalhos de teclado (pesquisa Ctrl+K) na página institucional.
 */

document.addEventListener('DOMContentLoaded', () => {
  initScrollAnimations();
  initLazyLoading();
  initSearchModal();
  initHeaderEffects();
  initAuthModals();
});

/**
 * 1. Animações de Entrada ao Scroll (Intersection Observer)
 */
function initScrollAnimations() {
  const observerOptions = {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  };

  const scrollObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-active');
        // Uma vez animado, não precisamos mais observar
        scrollObserver.unobserve(entry.target);
      }
    });
  }, observerOptions);

  // Selecionar elementos para observar
  const animElements = document.querySelectorAll(
    '.course-card, .beneficio-row, .sobre-card, .section-header, .cta-final-container'
  );
  
  animElements.forEach((el, index) => {
    // Adiciona classe de animação base
    el.classList.add('animate-ready');
    // Adiciona delay incremental (stagger effect) para elementos vizinhos
    const delayClass = `animate-delay-${(index % 3) + 1}`;
    el.classList.add(delayClass);
    
    scrollObserver.observe(el);
  });
}

/**
 * 2. Lazy Loading de Imagens de Fundo (Curso Thumbnails)
 */
function initLazyLoading() {
  const bgObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const el = entry.target;
        if (el.dataset.bg) {
          el.style.backgroundImage = `url('${el.dataset.bg}')`;
          el.classList.add('bg-loaded');
        }
        bgObserver.unobserve(el);
      }
    });
  }, {
    rootMargin: '100px' // Pré-carrega 100px antes de entrar na tela
  });

  const lazyBgs = document.querySelectorAll('.lazy-load-bg');
  lazyBgs.forEach(el => {
    bgObserver.observe(el);
  });
}

/**
 * 3. Modal de Pesquisa Rápida (Ctrl + K)
 */
function initSearchModal() {
  const modal = document.getElementById('search-modal');
  const input = document.getElementById('search-input');
  const resultsContainer = document.getElementById('search-results-list');
  const btnClose = document.getElementById('btn-close-search');

  if (!modal || !input || !resultsContainer) return;

  const mockCourses = [
    { title: 'Direito Notarial e Registral', url: './detalhes-direito-notarial.html', desc: 'A base teórica e as normas práticas essenciais para atuação no extrajudicial.' },
    { title: 'LGPD para Cartórios', url: './detalhes-lgpd-cartorios.html', desc: 'Adaptação de serventias extrajudiciais à Lei Geral de Proteção de Dados.' },
    { title: 'Tecnologia para Serventias', url: './painel-aluno.html', desc: 'Modernização, conformidade técnica e segurança da informação.' },
    { title: 'Gestão de Cartórios', url: './painel-aluno.html', desc: 'Liderança, eficiência operacional e governança de serventias.' }
  ];

  const openSearch = () => {
    modal.classList.add('open');
    input.value = '';
    renderResults('');
    setTimeout(() => input.focus(), 100);
    document.body.style.overflow = 'hidden'; // Evita scroll do fundo
  };

  const closeSearch = () => {
    modal.classList.remove('open');
    document.body.style.overflow = '';
  };

  // Atalho de Teclado Ctrl+K / Cmd+K
  document.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      openSearch();
    }
    if (e.key === 'Escape' && modal.classList.contains('open')) {
      closeSearch();
    }
  });

  if (btnClose) {
    btnClose.addEventListener('click', closeSearch);
  }

  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeSearch();
    }
  });

  input.addEventListener('input', (e) => {
    renderResults(e.target.value);
  });

  function renderResults(query) {
    resultsContainer.innerHTML = '';
    const cleanQuery = query.toLowerCase().trim();
    const filtered = mockCourses.filter(c => c.title.toLowerCase().includes(cleanQuery));

    if (filtered.length === 0) {
      resultsContainer.innerHTML = '<div style="padding: 16px; text-align: center; color: var(--text-secondary); font-size: 0.9rem;">Nenhum curso encontrado.</div>';
      return;
    }

    filtered.forEach(c => {
      const item = document.createElement('a');
      item.href = c.url;
      item.className = 'search-item-hover';
      item.style.display = 'block';
      item.style.padding = '12px var(--space-md)';
      item.style.borderRadius = 'var(--radius-sm)';
      item.style.border = '1px solid rgba(11, 27, 94, 0.06)';
      item.style.textDecoration = 'none';
      item.style.color = 'var(--text-primary)';
      item.style.transition = 'all 0.2s';
      
      item.innerHTML = `
        <div style="font-weight: 700; color: var(--blue-deep); font-size: 0.95rem; margin-bottom: 2px;">${c.title}</div>
        <div style="font-size: 0.8rem; color: var(--text-secondary); line-height: 1.4;">${c.desc}</div>
      `;
      
      resultsContainer.appendChild(item);
    });
  }
}

/**
 * 4. Efeito de Scroll no Header (compensa e otimiza o Web Component)
 */
function initHeaderEffects() {
  const header = document.querySelector('.landing-header');
  if (!header) return;

  const checkScroll = () => {
    if (window.scrollY > 80) {
      header.classList.add('solid');
    } else {
      header.classList.remove('solid');
    }
  };

  window.addEventListener('scroll', checkScroll, { passive: true });
  checkScroll();
}

/**
 * 5. Gerenciamento de Modais e Fluxo de Autenticação (Real)
 */
function initAuthModals() {
  const authModal = document.getElementById('auth-modal');
  const registerModal = document.getElementById('register-modal');
  const recoverModal = document.getElementById('recover-modal');
  
  const btnCloseAuth = document.getElementById('btn-close-auth');
  const btnCloseRegister = document.getElementById('btn-close-register');
  const btnCloseRecover = document.getElementById('btn-close-recover');
  
  const linkToRegister = document.getElementById('link-to-register');
  const linkToLogin = document.getElementById('link-to-login');
  const linkToRecover = document.getElementById('link-to-recover');
  const linkToLoginRecover = document.getElementById('link-to-login-recover');
  
  const authForm = document.getElementById('auth-form');
  const registerForm = document.getElementById('register-form');
  const recoverForm = document.getElementById('recover-form');

  if (!authModal || !registerModal) return;

  let activeLoginType = "student"; // "student" | "staff"
  let activeRegisterType = "student"; // "student" | "staff"
  let activeRecoverType = "student"; // "student" | "staff"

  // Elementos das Tabs de Recuperação
  const tabRecoverStudent = document.getElementById("tab-recover-student");
  const tabRecoverStaff = document.getElementById("tab-recover-staff");
  const recoverStudentField = document.getElementById("recover-student-field");
  const recoverStaffField = document.getElementById("recover-staff-field");

  // Elementos das Tabs de Login
  const tabLoginStudent = document.getElementById("tab-login-student");
  const tabLoginStaff = document.getElementById("tab-login-staff");
  const loginStudentField = document.getElementById("login-student-field");
  const loginStaffFields = document.getElementById("login-staff-fields");

  // Elementos das Tabs de Cadastro
  const tabRegisterStudent = document.getElementById("tab-register-student");
  const tabRegisterStaff = document.getElementById("tab-register-staff");
  const registerStudentField = document.getElementById("register-student-field");
  const registerStaffFields = document.getElementById("register-staff-fields");

  // Lógica de alternância de Tabs no Login
  if (tabLoginStudent && tabLoginStaff) {
    tabLoginStudent.addEventListener("click", () => {
      activeLoginType = "student";
      tabLoginStudent.classList.add("active");
      tabLoginStudent.style.color = "var(--blue-deep)";
      tabLoginStudent.style.borderBottom = "2px solid var(--blue-deep)";
      tabLoginStaff.classList.remove("active");
      tabLoginStaff.style.color = "var(--text-secondary)";
      tabLoginStaff.style.borderBottom = "none";
      
      if (loginStudentField) loginStudentField.style.display = "flex";
      if (loginStaffFields) loginStaffFields.style.display = "none";
      
      // Ajustar inputs required
      const cpfInput = document.getElementById("auth-cpf");
      const emailInput = document.getElementById("auth-email");
      const studentPassInput = document.getElementById("auth-student-password");
      const passInput = document.getElementById("auth-password");
      if (cpfInput) cpfInput.required = true;
      if (emailInput) emailInput.required = false;
      if (studentPassInput) studentPassInput.required = true;
      if (passInput) passInput.required = false;
    });

    tabLoginStaff.addEventListener("click", () => {
      activeLoginType = "staff";
      tabLoginStaff.classList.add("active");
      tabLoginStaff.style.color = "var(--blue-deep)";
      tabLoginStaff.style.borderBottom = "2px solid var(--blue-deep)";
      tabLoginStudent.classList.remove("active");
      tabLoginStudent.style.color = "var(--text-secondary)";
      tabLoginStudent.style.borderBottom = "none";
      
      if (loginStudentField) loginStudentField.style.display = "none";
      if (loginStaffFields) loginStaffFields.style.display = "flex";
      
      // Ajustar inputs required
      const cpfInput = document.getElementById("auth-cpf");
      const emailInput = document.getElementById("auth-email");
      const studentPassInput = document.getElementById("auth-student-password");
      const passInput = document.getElementById("auth-password");
      if (cpfInput) cpfInput.required = false;
      if (emailInput) emailInput.required = true;
      if (studentPassInput) studentPassInput.required = false;
      if (passInput) passInput.required = true;
    });
  }

  // Lógica de alternância de Tabs no Cadastro
  if (tabRegisterStudent && tabRegisterStaff) {
    tabRegisterStudent.addEventListener("click", () => {
      activeRegisterType = "student";
      tabRegisterStudent.classList.add("active");
      tabRegisterStudent.style.color = "var(--blue-deep)";
      tabRegisterStudent.style.borderBottom = "2px solid var(--blue-deep)";
      tabRegisterStaff.classList.remove("active");
      tabRegisterStaff.style.color = "var(--text-secondary)";
      tabRegisterStaff.style.borderBottom = "none";
      
      if (registerStudentField) registerStudentField.style.display = "flex";
      if (registerStaffFields) registerStaffFields.style.display = "none";
      
      // Ajustar inputs required
      const cpfInput = document.getElementById("register-cpf");
      const cartorioInput = document.getElementById("register-cartorio");
      const studentPassInput = document.getElementById("register-student-password");
      const studentConfirmInput = document.getElementById("register-student-confirm-password");
      const studentRecoveryInput = document.getElementById("register-student-recovery");
      const emailInput = document.getElementById("register-email");
      const passInput = document.getElementById("register-password");
      if (cpfInput) cpfInput.required = true;
      if (cartorioInput) cartorioInput.required = true;
      if (studentPassInput) studentPassInput.required = true;
      if (studentConfirmInput) studentConfirmInput.required = true;
      if (studentRecoveryInput) studentRecoveryInput.required = true;
      if (emailInput) emailInput.required = false;
      if (passInput) passInput.required = false;
    });

    tabRegisterStaff.addEventListener("click", () => {
      activeRegisterType = "staff";
      tabRegisterStaff.classList.add("active");
      tabRegisterStaff.style.color = "var(--blue-deep)";
      tabRegisterStaff.style.borderBottom = "2px solid var(--blue-deep)";
      tabRegisterStudent.classList.remove("active");
      tabRegisterStudent.style.color = "var(--text-secondary)";
      tabRegisterStudent.style.borderBottom = "none";
      
      if (registerStudentField) registerStudentField.style.display = "none";
      if (registerStaffFields) registerStaffFields.style.display = "flex";
      
      // Ajustar inputs required
      const cpfInput = document.getElementById("register-cpf");
      const cartorioInput = document.getElementById("register-cartorio");
      const studentPassInput = document.getElementById("register-student-password");
      const studentConfirmInput = document.getElementById("register-student-confirm-password");
      const studentRecoveryInput = document.getElementById("register-student-recovery");
      const emailInput = document.getElementById("register-email");
      const passInput = document.getElementById("register-password");
      if (cpfInput) cpfInput.required = false;
      if (cartorioInput) cartorioInput.required = false;
      if (studentPassInput) studentPassInput.required = false;
      if (studentConfirmInput) studentConfirmInput.required = false;
      if (studentRecoveryInput) studentRecoveryInput.required = false;
      if (emailInput) emailInput.required = true;
      if (passInput) passInput.required = true;
    });
  }

  // Lógica de alternância de Tabs na Recuperação
  if (tabRecoverStudent && tabRecoverStaff) {
    tabRecoverStudent.addEventListener("click", () => {
      activeRecoverType = "student";
      tabRecoverStudent.classList.add("active");
      tabRecoverStudent.style.color = "var(--blue-deep)";
      tabRecoverStudent.style.borderBottom = "2px solid var(--blue-deep)";
      tabRecoverStaff.classList.remove("active");
      tabRecoverStaff.style.color = "var(--text-secondary)";
      tabRecoverStaff.style.borderBottom = "none";
      
      if (recoverStudentField) recoverStudentField.style.display = "flex";
      if (recoverStaffField) recoverStaffField.style.display = "none";
      
      const cpfInput = document.getElementById("recover-cpf");
      const emailInput = document.getElementById("recover-email");
      if (cpfInput) cpfInput.required = true;
      if (emailInput) emailInput.required = false;
    });

    tabRecoverStaff.addEventListener("click", () => {
      activeRecoverType = "staff";
      tabRecoverStaff.classList.add("active");
      tabRecoverStaff.style.color = "var(--blue-deep)";
      tabRecoverStaff.style.borderBottom = "2px solid var(--blue-deep)";
      tabRecoverStudent.classList.remove("active");
      tabRecoverStudent.style.color = "var(--text-secondary)";
      tabRecoverStudent.style.borderBottom = "none";
      
      if (recoverStudentField) recoverStudentField.style.display = "none";
      if (recoverStaffField) recoverStaffField.style.display = "flex";
      
      const cpfInput = document.getElementById("recover-cpf");
      const emailInput = document.getElementById("recover-email");
      if (cpfInput) cpfInput.required = false;
      if (emailInput) emailInput.required = true;
    });
  }

  let captchaAnswer = 0;

  function generateCaptcha() {
    const num1 = Math.floor(Math.random() * 9) + 1;
    const num2 = Math.floor(Math.random() * 9) + 1;
    captchaAnswer = num1 + num2;
    const questionEl = document.getElementById("captcha-question");
    if (questionEl) {
      questionEl.innerText = `${num1} + ${num2} =`;
    }
    const inputEl = document.getElementById("register-captcha-input");
    if (inputEl) {
      inputEl.value = "";
    }
  }

  const btnRefreshCaptcha = document.getElementById("btn-refresh-captcha");
  if (btnRefreshCaptcha) {
    btnRefreshCaptcha.addEventListener("click", generateCaptcha);
  }

  const openAuth = () => {
    authModal.classList.add('open');
    registerModal.classList.remove('open');
    if (recoverModal) recoverModal.classList.remove('open');
    document.body.style.overflow = 'hidden';
    if (tabLoginStudent) tabLoginStudent.click();
  };

  const openRegister = () => {
    registerModal.classList.add('open');
    authModal.classList.remove('open');
    if (recoverModal) recoverModal.classList.remove('open');
    document.body.style.overflow = 'hidden';
    if (tabRegisterStudent) tabRegisterStudent.click();
    generateCaptcha();
  };

  const openRecover = () => {
    authModal.classList.remove('open');
    registerModal.classList.remove('open');
    if (recoverModal) recoverModal.classList.add('open');
    document.body.style.overflow = 'hidden';
    if (tabRecoverStudent) tabRecoverStudent.click();
  };

  const closeAll = () => {
    authModal.classList.remove('open');
    registerModal.classList.remove('open');
    if (recoverModal) recoverModal.classList.remove('open');
    document.body.style.overflow = '';
  };

  // Delegar cliques nos botões de cabeçalho do Web Component
  document.addEventListener('click', (e) => {
    const target = e.target.closest('#btn-login-desktop, #btn-login-mobile, #btn-register-desktop, #btn-register-mobile');
    if (!target) return;
    
    e.preventDefault();
    if (target.id.includes('login')) {
      openAuth();
    } else {
      openRegister();
    }
  });

  if (btnCloseAuth) btnCloseAuth.addEventListener('click', closeAll);
  if (btnCloseRegister) btnCloseRegister.addEventListener('click', closeAll);
  if (btnCloseRecover) btnCloseRecover.addEventListener('click', closeAll);
  if (linkToRegister) linkToRegister.addEventListener('click', (e) => { e.preventDefault(); openRegister(); });
  if (linkToLogin) linkToLogin.addEventListener('click', (e) => { e.preventDefault(); openAuth(); });
  if (linkToRecover) linkToRecover.addEventListener('click', (e) => { e.preventDefault(); openRecover(); });
  if (linkToLoginRecover) linkToLoginRecover.addEventListener('click', (e) => { e.preventDefault(); openAuth(); });

  // Fechar clicando no fundo escuro
  window.addEventListener('click', (e) => {
    if (e.target === authModal || e.target === registerModal || e.target === recoverModal) {
      closeAll();
    }
  });

  // Form de Login
  if (authForm) {
    authForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = authForm.querySelector('button[type="submit"]');
      const originalText = submitBtn.innerText;

      try {
        submitBtn.disabled = true;
        submitBtn.innerText = "Entrando...";

        let user;
        if (activeLoginType === "student") {
          const cpf = document.getElementById('auth-cpf').value;
          const password = document.getElementById('auth-student-password').value;
          const credential = await signInStudent(cpf, password);
          user = credential.user;
        } else {
          const email = document.getElementById('auth-email').value;
          const password = document.getElementById('auth-password').value;
          const credential = await signInStaff(email, password);
          user = credential.user;
        }

        const profile = await getUserProfile(user.uid);
        const role = profile?.role || "colaborador";
        
        const redirectMap = {
          "colaborador": "./painel-aluno.html",
          "instrutor": "./painel-instrutor.html",
          "admin": "./painel-admin.html"
        };
        
        window.location.href = redirectMap[role] || "./painel-aluno.html";
      } catch (error) {
        console.error("Erro de login:", error);
        let msg = "Verifique seus dados ou conexão.";
        if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
          msg = "CPF, e-mail ou senha inválidos.";
        }
        showToast("Erro ao entrar: " + msg, "error");
        submitBtn.disabled = false;
        submitBtn.innerText = originalText;
      }
    });
  }

  // Form de Cadastro
  if (registerForm) {
    registerForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = registerForm.querySelector('button[type="submit"]');
      const originalText = submitBtn.innerText;

      // Validar CAPTCHA
      const captchaInput = document.getElementById('register-captcha-input');
      const captchaVal = parseInt(captchaInput ? captchaInput.value : "0", 10);
      if (captchaVal !== captchaAnswer) {
        showToast("A verificação de segurança (soma) está incorreta. Tente novamente.", "error");
        generateCaptcha();
        return;
      }

      const name = document.getElementById('register-name').value.trim();

      try {
        submitBtn.disabled = true;
        submitBtn.innerText = "Criando conta...";

        let user;
        if (activeRegisterType === "student") {
          const cpf = document.getElementById('register-cpf').value;
          const senha = document.getElementById('register-student-password').value;
          const confirmar = document.getElementById('register-student-confirm-password').value;
          const serventia = document.getElementById('register-cartorio').value;
          const recoveryEmail = document.getElementById('register-student-recovery').value.trim();

          if (senha !== confirmar) {
            showToast("As senhas não conferem. Tente novamente.", "error");
            submitBtn.disabled = false;
            submitBtn.innerText = originalText;
            return;
          }

          const result = await signUpStudent(name, cpf, senha, serventia, recoveryEmail);
          user = result.user;
        } else {
          const email = document.getElementById('register-email').value;
          const password = document.getElementById('register-password').value;
          const role = document.getElementById('register-role').value;

          if (password.length < 6) {
            showToast("A senha deve conter no mínimo 6 caracteres.", "error");
            submitBtn.disabled = false;
            submitBtn.innerText = originalText;
            return;
          }
          const result = await signUpStaff(name, email, password, role);
          user = result.user;
        }

        if (!user) {
          throw new Error("Falha ao obter usuário do cadastro.");
        }

        const profile = await getUserProfile(user.uid);
        const role = profile?.role || "colaborador";
        
        const redirectMap = {
          "colaborador": "./painel-aluno.html",
          "instrutor": "./painel-instrutor.html",
          "admin": "./painel-admin.html"
        };
        
        showToast("Conta criada com sucesso! Redirecionando...", "success");
        setTimeout(() => {
          window.location.href = redirectMap[role] || "./painel-aluno.html";
        }, 1500);
      } catch (error) {
        console.error("Erro de cadastro:", error);
        let msg = "Verifique seus dados ou conexão.";
        if (error.code === 'auth/email-already-in-use') {
          msg = activeRegisterType === "student"
            ? "Este CPF ou e-mail de recuperação já está cadastrado na plataforma."
            : "Este e-mail já está cadastrado na plataforma.";
        } else if (error.code === 'auth/invalid-email') {
          msg = "E-mail inválido.";
        } else if (error.code === 'auth/weak-password') {
          msg = "A senha deve conter no mínimo 6 caracteres.";
        } else if (error.message) {
          msg = error.message;
        }
        showToast("Erro ao cadastrar: " + msg, "error");
        submitBtn.disabled = false;
        submitBtn.innerText = originalText;
        generateCaptcha();
      }
    });
  }

  // Form de Recuperação de Senha
  if (recoverForm) {
    recoverForm.addEventListener('submit', async (e) => {
      e.preventDefault();
      const submitBtn = recoverForm.querySelector('button[type="submit"]');
      const originalText = submitBtn.innerText;

      try {
        submitBtn.disabled = true;
        submitBtn.innerText = "Enviando...";

        if (activeRecoverType === "student") {
          const cpf = document.getElementById('recover-cpf').value;
          await sendPasswordResetForStudent(cpf);
        } else {
          const email = document.getElementById('recover-email').value;
          await sendPasswordResetForStaff(email);
        }

        showToast("Se houver uma conta com esses dados, enviaremos um link de recuperação para o e-mail cadastrado.", "success");
        openAuth();
      } catch (error) {
        console.error("Erro na recuperação de senha:", error);
        let msg = "Não foi possível solicitar a recuperação.";
        if (error.code === 'auth/invalid-email' || error.code === 'auth/missing-email') {
          msg = "E-mail inválido.";
        } else if (error.message) {
          msg = error.message;
        }
        showToast("Erro: " + msg, "error");
        submitBtn.disabled = false;
        submitBtn.innerText = originalText;
      }
    });
  }

  // Verificar se há ações na URL (redirecionados das páginas de propaganda)
  const urlParams = new URLSearchParams(window.location.search);
  const action = urlParams.get('action');
  if (action === 'login') {
    setTimeout(openAuth, 100);
  } else if (action === 'register') {
    setTimeout(openRegister, 100);
  }
}
